import { mulberry32 } from '@halvard/rules';
import { TILE_SIZE } from '../geometry.js';

/** A fracture line on a slab's top, in tile-local x/z, with its width at each point. */
export interface CrackLine {
  points: [number, number][];
  widths: number[];
}

const HALF = (TILE_SIZE / 2) * 0.94;

function clampToTile(v: number): number {
  return Math.min(Math.max(v, -HALF), HALF);
}

/**
 * The fracture lines of a cracked square, like ice struck at one point: thin, zig-zagging
 * branches run from near the middle out to the edges of the slab, thinning as they go and
 * forking, with a few short ring cracks between neighbouring branches. The same square on the
 * same lake always cracks the same way.
 */
export function crackPattern(seed: number, r: number, c: number): CrackLine[] {
  const rng = mulberry32((seed ^ Math.imul(r * 31 + c + 1, 0x9e3779b1)) >>> 0);
  const origin: [number, number] = [(rng() - 0.5) * 0.2, (rng() - 0.5) * 0.2];
  const branches = 3 + Math.floor(rng() * 3);
  const start = rng() * Math.PI * 2;
  const mains: CrackLine[] = [];
  const lines: CrackLine[] = [];
  for (let b = 0; b < branches; b++) {
    const angle = start + (b / branches) * Math.PI * 2 + (rng() - 0.5) * 0.7;
    const main = jagged(rng, origin, angle, HALF * (1.1 + rng() * 0.4), 0.036, 0.006);
    mains.push(main);
    lines.push(main);
    // Forks from part-way along, at a slant, and hairlines off those.
    for (let f = 0; f < 2; f++) {
      if (rng() > 0.7 || main.points.length < 3) continue;
      const at = 1 + Math.floor(rng() * (main.points.length - 2));
      const from = main.points[at] ?? origin;
      const side = rng() < 0.5 ? -1 : 1;
      const width = (main.widths[at] ?? 0.02) * 0.65;
      const fork = jagged(
        rng,
        from,
        angle + side * (0.45 + rng() * 0.6),
        HALF * (0.3 + rng() * 0.35),
        width,
        0.004,
      );
      lines.push(fork);
      const tip = fork.points[Math.floor(fork.points.length / 2)];
      if (tip && rng() < 0.5) {
        lines.push(
          jagged(rng, tip, angle - side * (0.6 + rng() * 0.5), HALF * 0.2, width * 0.6, 0.003),
        );
      }
    }
  }
  // Ring cracks: short bridges between neighbouring branches, near the point of impact.
  for (let b = 0; b < mains.length; b++) {
    if (rng() > 0.45) continue;
    const at = 1 + Math.floor(rng() * 2);
    const p = mains[b]?.points[at];
    const q = mains[(b + 1) % mains.length]?.points[at];
    if (!p || !q) continue;
    const mid: [number, number] = [
      clampToTile((p[0] + q[0]) / 2 + (rng() - 0.5) * 0.06),
      clampToTile((p[1] + q[1]) / 2 + (rng() - 0.5) * 0.06),
    ];
    lines.push({ points: [p, mid, q], widths: [0.008, 0.006, 0.004] });
  }
  return lines;
}

function jagged(
  rng: () => number,
  from: [number, number],
  angle: number,
  length: number,
  startWidth: number,
  endWidth: number,
): CrackLine {
  const steps = 5 + Math.floor(rng() * 3);
  const points: [number, number][] = [from];
  const widths = [startWidth];
  let [x, z] = from;
  for (let i = 1; i <= steps; i++) {
    // Zig-zag about the branch's direction, so it stays a line, not a curl.
    const heading = angle + (rng() - 0.5) * 1.1;
    const step = (length / steps) * (0.7 + rng() * 0.6);
    x = clampToTile(x + Math.cos(heading) * step);
    z = clampToTile(z + Math.sin(heading) * step);
    points.push([x, z]);
    widths.push(startWidth + (endWidth - startWidth) * (i / steps));
    if (Math.abs(x) >= HALF || Math.abs(z) >= HALF) break;
  }
  return { points, widths };
}

/**
 * The lines as flat, tapered ribbons on y = 0: a position array (x, y, z per vertex) and the
 * triangle indices, ready for a BufferGeometry. `widen` scales every width.
 */
export function crackRibbons(
  lines: readonly CrackLine[],
  widen = 1,
): {
  positions: Float32Array;
  indices: number[];
} {
  const positions: number[] = [];
  const indices: number[] = [];
  for (const { points, widths } of lines) {
    const base = positions.length / 3;
    points.forEach(([x, z], i) => {
      const [px, pz] = points[Math.max(i - 1, 0)] ?? [x, z];
      const [nx, nz] = points[Math.min(i + 1, points.length - 1)] ?? [x, z];
      const dx = nx - px;
      const dz = nz - pz;
      const len = Math.hypot(dx, dz) || 1;
      const half = ((widths[i] ?? 0) * widen) / 2;
      // Perpendicular to the line's direction at this point.
      const ox = (-dz / len) * half;
      const oz = (dx / len) * half;
      positions.push(x + ox, 0, z + oz, x - ox, 0, z - oz);
    });
    for (let i = 0; i < points.length - 1; i++) {
      const a = base + i * 2;
      indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
    }
  }
  return { positions: new Float32Array(positions), indices };
}

/**
 * How a cracked slab shivers at `time` seconds: a short burst of trembling every couple of
 * seconds, stronger as the thaw nears (`urgency` from 0 to 1). `phase` keeps cracked squares
 * from shaking in step. Returns the x/z offset and a small twist about the vertical.
 */
export function crackShake(
  time: number,
  phase: number,
  urgency: number,
): { x: number; z: number; twist: number } {
  const period = 2.6 - urgency * 1.1;
  const t = (time + phase) % period;
  const burst = 0.45;
  if (t > burst) return { x: 0, z: 0, twist: 0 };
  const envelope = Math.sin((t / burst) * Math.PI);
  const amp = (0.008 + urgency * 0.012) * envelope;
  return {
    x: Math.sin(t * 71) * amp,
    z: Math.sin(t * 53 + 1.3) * amp,
    twist: Math.sin(t * 61 + 0.7) * amp * 0.8,
  };
}
